import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tabs,
  Tab,
  Button,
  Divider,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Support as SupportIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ThemeBackground from '../components/Layout/ThemeBackground';
import CredentialExpiryAlerts from '../components/Dashboard/CredentialExpiryAlerts';
import PaymentDashboard from '../components/admin/PaymentDashboard';
import SystemNotificationBanner from '../components/Notifications/SystemNotificationBanner';
import FeedbackAnalytics from '../components/admin/FeedbackAnalytics';
import DataIntegrityManagement from '../components/admin/DataIntegrityManagement';

// ... (Keep existing interfaces: DashboardMetrics, OutstandingPayment, DbsAlert)
interface DashboardMetrics {
  clients: { total: number; active: number; newThisMonth: number; pendingAssessments: number; completedAssessments: number; };
  counsellors: { total: number; active: number; pendingApplications: number; atCapacity: number; nearCapacity: number; };
  sessions: { total: number; completed: number; upcoming: number; thisMonth: number; };
  dbs: { expiringIn30Days: number; expiringIn60Days: number; expiringIn90Days: number; };
  insurance: { expiringIn30Days: number; expiringIn60Days: number; expiringIn90Days: number; };
  payments: { outstandingAmount: number; outstandingCount: number; revenueThisMonth: number; };
  security: { totalUsers: number; mfaEnabledUsers: number; mfaAdoptionRate: number; recentMfaRegistrations: number; };
  capacity: {
    counsellors: Array<{ 
      _id: string; 
      firstName: string; 
      lastName: string; 
      maxClients: number; 
      currentClientCount: number; 
      utilizationRate: number;
      activeClients?: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        ClientRef: string;
      }>;
    }>;
    atCapacity: number;
    nearCapacity: number;
  };
  recentActivity: {
    clients: Array<{ _id: string; firstName: string; lastName: string; createdAt: string; }>;
    sessions: Array<{ _id: string; clientId?: { firstName: string; lastName: string; }; sessionDate?: string; createdAt: string; }>;
  };
}

interface OutstandingPayment {
  _id: string; clientName: string; clientRef: string; totalOutstanding: number; transactionCount: number; oldestTransaction: string;
}

interface DbsAlert {
  counsellorId: string; name: string; dbsNumber: string; expiryDate: string; daysUntilExpiry: number; urgency: 'high' | 'medium' | 'low';
}

const MetricCard: React.FC<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: string;
}> = ({ title, value, icon, color, subtitle, trend }) => (
  <Card sx={{ 
    height: '100%', 
    backgroundColor: 'rgba(255, 255, 255, 0.05)', 
    backdropFilter: 'blur(10px)', 
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': { transform: 'translateY(-2px)' }
  }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.95)' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Typography variant="caption" sx={{ color: 'rgba(76, 175, 80, 0.9)', display: 'block', mt: 0.5 }}>
              {trend}
            </Typography>
          )}
        </Box>
        <Box sx={{ color: color, opacity: 1, filter: 'brightness(1.3)', p: 1, borderRadius: '50%', backgroundColor: `${color}20` }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedCounsellor, setExpandedCounsellor] = useState<string | null>(null);
  const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
  const [complianceRunning, setComplianceRunning] = useState(false);
  const [complianceResult, setComplianceResult] = useState<{
    counsellorsChecked: number;
    statusUpdated: number;
    suspended: number;
    suspendedCounsellors?: Array<{ counsellorId: string; name: string; reason: string }>;
    errors: number;
  } | null>(null);
  const [complianceError, setComplianceError] = useState<string | null>(null);

  const runComplianceCheck = async () => {
    setComplianceRunning(true);
    setComplianceError(null);
    try {
      const res = await api.post('/api/counsellors/compliance-check');
      setComplianceResult(res.data);
    } catch (err: any) {
      setComplianceError(err.response?.data?.error || 'Compliance check failed');
    } finally {
      setComplianceRunning(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const metricsRes = await api.get('/api/dashboard/metrics');
      setMetrics(metricsRes.data);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    }
  };

  useEffect(() => {
    if (user?.role === 'administrator') {
      const loadInitialData = async () => {
        setLoading(true);
        await fetchDashboardData();
        setLoading(false);
      };
      loadInitialData();
    }
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'administrator') return;
    const refreshInterval = setInterval(() => { fetchDashboardData(); }, 30000);
    return () => clearInterval(refreshInterval);
  }, [user]);

  const handleSupportClick = async () => {
    try {
      const context = {
        currentUrl: window.location.href,
        pathname: window.location.pathname,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        timestamp: new Date().toISOString()
      };
      const response = await api.post<{ supportUrl: string; sessionId: string }>('/api/support/session', { context });
      if (!response.data?.supportUrl) throw new Error('No support URL returned from server');
      window.location.href = response.data.supportUrl;
    } catch (error) {
      console.error('[SUPPORT] Error:', error);
      alert(`Failed to open support portal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (user?.role !== 'administrator') {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8">
          <div className="glass-card p-6">
            <Alert severity="error">Access denied. Only administrators can view the dashboard.</Alert>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-screen">
          <CircularProgress sx={{ color: 'white' }} />
        </div>
      </ThemeBackground>
    );
  }

  if (error) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8">
          <div className="glass-card p-6">
            <Alert severity="error" action={
              <IconButton color="inherit" size="small" onClick={fetchDashboardData}><RefreshIcon /></IconButton>
            }>{error}</Alert>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  if (!metrics) return null;

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  const formatDate = (dateString: string | null) => dateString ? new Date(dateString).toLocaleDateString('en-GB') : 'N/A';

  // Helper for interactive alert cards
  const AlertCard = ({ title, content, icon, colorHex, bgColor, onClickAction }: any) => (
    <Card 
      onClick={onClickAction}
      sx={{ 
        backgroundColor: bgColor, 
        backdropFilter: 'blur(10px)', 
        border: `1px solid ${colorHex}40`,
        cursor: 'pointer',
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${colorHex}20`,
          borderColor: colorHex
        }
      }}
    >
      <CardContent sx={{ pb: '16px !important' }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Box sx={{ p: 1, borderRadius: '8px', backgroundColor: `${colorHex}20` }}>
            {icon}
          </Box>
          <Box flexGrow={1}>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '1.1rem' }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
              {content}
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: 'rgba(255,255,255,0.3)', alignSelf: 'center' }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
            
            {user && (
              <SystemNotificationBanner
                appId="counselling"
                clientId="revo"
                environment={process.env.NODE_ENV === 'production' ? 'production' : 'development'}
                userId={user._id}
                userRole={user.role}
              />
            )}
            
            {/* Header Section */}
            {/* Header Section - consistent with MTDDashboard/ArchivedRecords */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div className="flex flex-col space-y-4">
                    <div className="flex space-x-4">
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '160px',
                          backgroundColor: '#2196f3',
                          '&:hover': { backgroundColor: '#1976d2' }
                        }}
                        onClick={() => navigate('/')}
                      >
                        Back to Home
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '120px',
                          backgroundColor: '#9c27b0',
                          '&:hover': { backgroundColor: '#7b1fa2' }
                        }}
                        onClick={fetchDashboardData}
                        startIcon={<RefreshIcon />}
                      >
                        Refresh
                      </Button>
                    </div>
                    <div className="flex space-x-4">
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '160px',
                          backgroundColor: '#43a047',
                          '&:hover': { backgroundColor: '#388e3c' }
                        }}
                        onClick={() => { setComplianceResult(null); setComplianceError(null); setComplianceDialogOpen(true); }}
                        startIcon={<SecurityIcon />}
                      >
                        Run Compliance Check
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '120px',
                          backgroundColor: '#ff9800',
                          '&:hover': { backgroundColor: '#f57c00' }
                        }}
                        onClick={handleSupportClick}
                        startIcon={<SupportIcon />}
                      >
                        Support
                      </Button>
                    </div>
                  </div>
                  <div className="text-right">
                    <Typography
                      variant="h3"
                      component="h1"
                      gutterBottom
                      className="theme-text-primary"
                      sx={{ fontFamily: 'Roboto', fontWeight: 800, fontSize: '2.5rem', letterSpacing: '0.5px' }}
                    >
                      Admin Dashboard
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      className="theme-text-secondary"
                      sx={{ fontWeight: 500, fontSize: '1.1rem' }}
                    >
                      System overview and operations
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Overview Section */}
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, fontWeight: 600 }}>
              At a Glance
            </Typography>
            <Grid container spacing={3} mb={6}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Clients"
                  value={metrics.clients.total}
                  icon={<PeopleIcon fontSize="large" />}
                  color="#64B5F6"
                  subtitle={`${metrics.clients.active} active`}
                  trend={`+${metrics.clients.newThisMonth} this month`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Active Counsellors"
                  value={metrics.counsellors.active}
                  icon={<PersonAddIcon fontSize="large" />}
                  color="#81C784"
                  subtitle={`${metrics.counsellors.total} total`}
                  trend={`${metrics.counsellors.pendingApplications} pending`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Sessions This Month"
                  value={metrics.sessions.thisMonth}
                  icon={<EventIcon fontSize="large" />}
                  color="#FFB74D"
                  subtitle={`${metrics.sessions.upcoming} upcoming`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Revenue This Month"
                  value={formatCurrency(metrics.payments.revenueThisMonth)}
                  icon={<PaymentIcon fontSize="large" />}
                  color="#CE93D8"
                  subtitle={`${metrics.payments.outstandingCount} outstanding`}
                />
              </Grid>
            </Grid>

            {/* Action Required Section */}
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, fontWeight: 600 }}>
              Action Required
            </Typography>
            <Grid container spacing={2} mb={5}>
              <Grid item xs={12} sm={6} lg={4}>
                <AlertCard 
                  title="DBS Expirations"
                  content={`${metrics.dbs.expiringIn30Days} in 30 days, ${metrics.dbs.expiringIn60Days} in 60 days`}
                  icon={<SecurityIcon sx={{ color: metrics.dbs.expiringIn30Days > 0 ? '#EF5350' : '#81C784' }} />}
                  colorHex={metrics.dbs.expiringIn30Days > 0 ? '#EF5350' : '#81C784'}
                  bgColor={metrics.dbs.expiringIn30Days > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'}
                  onClickAction={() => setActiveTab(0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <AlertCard 
                  title="Insurance Expirations"
                  content={`${metrics.insurance.expiringIn30Days} in 30 days, ${metrics.insurance.expiringIn60Days} in 60 days`}
                  icon={<SecurityIcon sx={{ color: metrics.insurance.expiringIn30Days > 0 ? '#EF5350' : '#81C784' }} />}
                  colorHex={metrics.insurance.expiringIn30Days > 0 ? '#EF5350' : '#81C784'}
                  bgColor={metrics.insurance.expiringIn30Days > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'}
                  onClickAction={() => setActiveTab(0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <AlertCard 
                  title="Pending Assessments"
                  content={`${metrics.clients.pendingAssessments} clients awaiting assessment`}
                  icon={<AssessmentIcon sx={{ color: metrics.clients.pendingAssessments > 0 ? '#FFB74D' : '#81C784' }} />}
                  colorHex={metrics.clients.pendingAssessments > 0 ? '#FFB74D' : '#81C784'}
                  bgColor={metrics.clients.pendingAssessments > 0 ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)'}
                  onClickAction={() => setActiveTab(2)}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={6}>
                <AlertCard 
                  title="Counsellor Capacity"
                  content={`${metrics.counsellors.atCapacity} at capacity, ${metrics.counsellors.nearCapacity} near capacity`}
                  icon={<TrendingUpIcon sx={{ color: metrics.counsellors.atCapacity > 0 ? '#EF5350' : '#81C784' }} />}
                  colorHex={metrics.counsellors.atCapacity > 0 ? '#EF5350' : '#81C784'}
                  bgColor={metrics.counsellors.atCapacity > 0 ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'}
                  onClickAction={() => setActiveTab(1)}
                />
              </Grid>
              <Grid item xs={12} sm={12} lg={6}>
                <AlertCard 
                  title="MFA Security Status"
                  content={`${metrics.security?.mfaEnabledUsers || 0}/${metrics.security?.totalUsers || 0} users (${(metrics.security?.mfaAdoptionRate || 0).toFixed(0)}%)`}
                  icon={<SecurityIcon sx={{ color: metrics.security?.mfaAdoptionRate < 50 ? '#EF5350' : metrics.security?.mfaAdoptionRate < 80 ? '#FFB74D' : '#81C784' }} />}
                  colorHex={metrics.security?.mfaAdoptionRate < 50 ? '#EF5350' : metrics.security?.mfaAdoptionRate < 80 ? '#FFB74D' : '#81C784'}
                  bgColor={metrics.security?.mfaAdoptionRate < 50 ? 'rgba(244, 67, 54, 0.1)' : metrics.security?.mfaAdoptionRate < 80 ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)'}
                  onClickAction={() => setActiveTab(5)}
                />
              </Grid>
            </Grid>

            {/* Detailed Tables Section */}
            <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&.Mui-selected': { color: '#64B5F6' }
                  },
                  '& .MuiTabs-indicator': { backgroundColor: '#64B5F6', height: 3 }
                }}
              >
                <Tab label="Credential Alerts" />
                <Tab label="Counsellor Capacity" />
                <Tab label="Recent Activity" />
                <Tab label="Payments" />
                <Tab label="Application Feedback" />
                <Tab label="Security" />
                <Tab label="Data Integrity" />
              </Tabs>
            </Box>

            <Box sx={{ minHeight: '400px' }}>
              {activeTab === 0 && <CredentialExpiryAlerts />}
              
              {activeTab === 1 && (
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom className="theme-text-primary">
                      Counsellor Capacity Overview
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                          <TableRow>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', width: '40px' }} />
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Counsellor</TableCell>
                            <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Current Clients</TableCell>
                            <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Max Clients</TableCell>
                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Utilization</TableCell>
                            <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {metrics.capacity.counsellors.map((counsellor) => (
                            <React.Fragment key={counsellor._id}>
                              <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                                <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => setExpandedCounsellor(expandedCounsellor === counsellor._id ? null : counsellor._id)}
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                  >
                                    {expandedCounsellor === counsellor._id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate(`/counsellors/${counsellor._id}`)}
                                    sx={{
                                      color: '#fafafa',
                                      textDecoration: 'none',
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                        color: '#ffffff'
                                      }
                                    }}
                                  >
                                    {`${counsellor.firstName} ${counsellor.lastName}`}
                                  </Link>
                                </TableCell>
                              <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{counsellor.currentClientCount}</TableCell>
                              <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{counsellor.maxClients}</TableCell>
                              <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(counsellor.utilizationRate * 100, 100)}
                                    sx={{ flexGrow: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.1)' }}
                                    color={
                                      counsellor.utilizationRate >= 1 ? 'error' :
                                      counsellor.utilizationRate >= 0.8 ? 'warning' : 'success'
                                    }
                                  />
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', minWidth: 35 }}>
                                    {Math.round(counsellor.utilizationRate * 100)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                <Chip
                                  label={counsellor.utilizationRate >= 1 ? 'At Capacity' : counsellor.utilizationRate >= 0.8 ? 'Near Capacity' : 'Available'}
                                  color={counsellor.utilizationRate >= 1 ? 'error' : counsellor.utilizationRate >= 0.8 ? 'warning' : 'success'}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                  <Collapse in={expandedCounsellor === counsellor._id} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 2 }}>
                                      <Typography variant="h6" gutterBottom component="div" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                        Active Clients
                                      </Typography>
                                      {counsellor.activeClients && counsellor.activeClients.length > 0 ? (
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Client Name</TableCell>
                                              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Client Ref</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {counsellor.activeClients.map((client: any) => (
                                              <TableRow key={client._id}>
                                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                                  <Link
                                                    component="button"
                                                    variant="body2"
                                                    onClick={() => navigate(`/client-details/${client._id}`)}
                                                    sx={{
                                                      color: '#fafafa',
                                                      textDecoration: 'none',
                                                      fontWeight: 500,
                                                      cursor: 'pointer',
                                                      '&:hover': {
                                                        textDecoration: 'underline',
                                                        color: '#ffffff'
                                                      }
                                                    }}
                                                  >
                                                    {`${client.firstName} ${client.lastName}`}
                                                  </Link>
                                                </TableCell>
                                                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                  {client.ClientRef || 'N/A'}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      ) : (
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                          No active clients
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {activeTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                          Recent Clients (Last 7 Days)
                        </Typography>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                        {metrics.recentActivity.clients.length === 0 ? (
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>No new clients in the last 7 days</Typography>
                        ) : (
                          metrics.recentActivity.clients.map((client) => (
                            <Box key={client._id} display="flex" justifyContent="space-between" py={1.5} sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{`${client.firstName} ${client.lastName}`}</Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatDate(client.createdAt)}</Typography>
                            </Box>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                          Recent Sessions (Last 7 Days)
                        </Typography>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                        {metrics.recentActivity.sessions.length === 0 ? (
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>No new sessions in the last 7 days</Typography>
                        ) : (
                          metrics.recentActivity.sessions.map((session) => (
                            <Box key={session._id} display="flex" justifyContent="space-between" py={1.5} sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>{`${session.clientId?.firstName || 'Unknown'} ${session.clientId?.lastName || 'Client'}`}</Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>{formatDate(session.sessionDate || session.createdAt)}</Typography>
                            </Box>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 3 && <PaymentDashboard />}
              {activeTab === 4 && <FeedbackAnalytics />}
              
              {activeTab === 5 && (
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, mb: 3 }}>
                      Multi-Factor Authentication (MFA) Security Overview
                    </Typography>
                    
                    <Grid container spacing={3}>
                      {/* MFA Adoption Summary */}
                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(100, 181, 246, 0.1)', border: '1px solid rgba(100, 181, 246, 0.3)' }}>
                          <Typography variant="h3" sx={{ color: '#64B5F6', fontWeight: 700, mb: 1 }}>
                            {(metrics.security?.mfaAdoptionRate || 0).toFixed(0)}%
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                            MFA Adoption Rate
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
                            {metrics.security?.mfaEnabledUsers || 0} of {metrics.security?.totalUsers || 0} users
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(129, 199, 132, 0.1)', border: '1px solid rgba(129, 199, 132, 0.3)' }}>
                          <Typography variant="h3" sx={{ color: '#81C784', fontWeight: 700, mb: 1 }}>
                            {metrics.security?.mfaEnabledUsers || 0}
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                            MFA Enabled Users
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
                            Using WebAuthn or TOTP
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(239, 83, 80, 0.1)', border: '1px solid rgba(239, 83, 80, 0.3)' }}>
                          <Typography variant="h3" sx={{ color: '#EF5350', fontWeight: 700, mb: 1 }}>
                            {(metrics.security?.totalUsers || 0) - (metrics.security?.mfaEnabledUsers || 0)}
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                            Users Without MFA
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
                            Potential security risk
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Security Recommendations */}
                      <Grid item xs={12}>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
                        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, mb: 2 }}>
                          Security Recommendations
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {(metrics.security?.mfaAdoptionRate || 0) < 50 && (
                            <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Critical: Low MFA Adoption</Typography>
                              <Typography variant="body2">Less than 50% of users have MFA enabled. Strongly encourage all users to enable MFA for enhanced security.</Typography>
                            </Alert>
                          )}
                          
                          {(metrics.security?.mfaAdoptionRate || 0) >= 50 && (metrics.security?.mfaAdoptionRate || 0) < 80 && (
                            <Alert severity="warning" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Moderate MFA Adoption</Typography>
                              <Typography variant="body2">Good progress! Continue encouraging remaining users to enable MFA to reach 80%+ adoption.</Typography>
                            </Alert>
                          )}
                          
                          {(metrics.security?.mfaAdoptionRate || 0) >= 80 && (
                            <Alert severity="success" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', color: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>Excellent MFA Adoption</Typography>
                              <Typography variant="body2">Great work! Your organization has strong security practices with {(metrics.security?.mfaAdoptionRate || 0).toFixed(0)}% MFA adoption.</Typography>
                            </Alert>
                          )}

                          <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(100, 181, 246, 0.05)', border: '1px solid rgba(100, 181, 246, 0.2)' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, mb: 1 }}>
                              💡 Best Practices
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              • Require MFA for all administrator accounts<br />
                              • Encourage WebAuthn (biometric) authentication for better user experience<br />
                              • Regularly review users without MFA and send reminders<br />
                              • Consider making MFA mandatory for sensitive operations
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
              
              {activeTab === 6 && <DataIntegrityManagement />}
            </Box>

          </div>
        </div>
      </div>

      {/* Compliance Check Dialog */}
      <Dialog
        open={complianceDialogOpen}
        onClose={() => { if (!complianceRunning) setComplianceDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run Compliance Check</DialogTitle>
        <DialogContent>
          {complianceRunning ? (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={24} />
              <DialogContentText>
                Checking every counsellor's DBS, insurance and work permit status…
              </DialogContentText>
            </Box>
          ) : complianceError ? (
            <Alert severity="error">{complianceError}</Alert>
          ) : complianceResult ? (
            <Box>
              <Alert severity={complianceResult.suspended > 0 || complianceResult.errors > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Checked {complianceResult.counsellorsChecked} counsellors — {complianceResult.statusUpdated} status
                {complianceResult.statusUpdated === 1 ? '' : 'es'} updated, {complianceResult.suspended} suspended
                {complianceResult.errors > 0 && `, ${complianceResult.errors} error${complianceResult.errors === 1 ? '' : 's'}`}
              </Alert>
              {complianceResult.suspendedCounsellors && complianceResult.suspendedCounsellors.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Suspended counsellors
                  </Typography>
                  <List dense>
                    {complianceResult.suspendedCounsellors.map((c) => (
                      <ListItem key={c.counsellorId} disableGutters>
                        <ListItemText
                          primary={c.name}
                          secondary={c.reason}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="body2" color="text.secondary">
                    Suspended counsellors cannot log in and cannot be reactivated until the issues above are resolved.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <DialogContentText>
              This re-verifies every counsellor's DBS, insurance and work permit status — the same check that runs
              nightly at 2 AM. Any counsellor currently <strong>Active</strong> but non-compliant (missing, expired
              or unverified credentials) will be <strong>suspended immediately</strong> and their login locked.
              Credentials that are merely expiring soon are flagged as warnings and do not suspend.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {complianceRunning ? null : complianceResult || complianceError ? (
            <Button onClick={() => setComplianceDialogOpen(false)} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={() => setComplianceDialogOpen(false)}>Cancel</Button>
              <Button onClick={runComplianceCheck} variant="contained" color="warning" startIcon={<SecurityIcon />}>
                Run Check
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </ThemeBackground>
  );
};

export default AdminDashboard;